import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchUserProfiles } from '../services/usersService';
import { useUserSearch, useProfilePictureUrls } from '../hooks/useUserSearch';
import { getRecentUsers, removeRecentUser, refreshRecentUsers } from '../utils/userProfiles';
import Button from '../components/Button';
import './UsersPage.css';

// A query that could be a uid: no whitespace, no "@", no "/"
const looksLikeUid = (query) => /^[^\s@/]+$/.test(query);

const UserCard = ({ user, pictureUrl, onPictureError, onRemove }) => (
  <Link to={`/users/${user.uid}`} className="user-card user-card-clickable">
    <div className="user-profile-container">
      {pictureUrl ? (
        <img
          src={pictureUrl}
          alt={user.name || 'User'}
          className="user-profile-picture"
          onError={() => onPictureError(user.uid)}
        />
      ) : (
        <div className="user-profile-initials">{user.initials || '?'}</div>
      )}
    </div>
    <div className="user-info">
      <h3 className="user-name">{user.name || user.uid}</h3>
      <p className="user-email">{user.email || user.uid}</p>
    </div>
    {onRemove && (
      <button
        className="user-remove-btn"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove(user.uid);
        }}
        title="Remove from recently viewed"
      >
        ×
      </button>
    )}
  </Link>
);

const UsersPage = () => {
  const { query, setQuery, results, searching, error, search } = useUserSearch();
  const [recentUsers, setRecentUsers] = useState(() => getRecentUsers());
  const searchInputRef = useRef(null);

  // Focus the search box without scrolling the page under the app header
  useEffect(() => {
    searchInputRef.current?.focus({ preventScroll: true });
  }, []);

  // Refresh names and pictures of recently viewed users once per visit
  useEffect(() => {
    const uids = getRecentUsers().map(u => u.uid);
    if (uids.length === 0) return;
    fetchUserProfiles(uids).then(profiles => {
      if (profiles.length > 0) setRecentUsers(refreshRecentUsers(profiles));
    });
  }, []);

  const shownUsers = results ?? recentUsers;
  const { pictureUrls, markPictureFailed } = useProfilePictureUrls(shownUsers);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    search();
  }, [search]);

  const handleRemoveRecent = useCallback((uid) => {
    setRecentUsers(removeRecentUser(uid));
  }, []);

  const trimmedQuery = query.trim();

  return (
    <div className="users-page">
      <div className="users-header">
        <h1>Users</h1>
      </div>
      <form className="users-search-container" onSubmit={handleSubmit}>
        <input
          type="text"
          className="users-search-input"
          placeholder="Search by name, email, or UID..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          ref={searchInputRef}
        />
        <Button type="submit" variant="primary" size="medium" disabled={searching || !trimmedQuery}>
          {searching ? 'Searching...' : 'Search'}
        </Button>
      </form>
      <div className="users-content">
        {results === null ? (
          <>
            <h2 className="users-section-title">Recently viewed</h2>
            {recentUsers.length === 0 ? (
              <div className="empty-users">
                <p>Search for a user by name, email, or UID.</p>
              </div>
            ) : (
              <div className="users-list">
                {recentUsers.map(user => (
                  <UserCard
                    key={user.uid}
                    user={user}
                    pictureUrl={pictureUrls.get(user.uid)}
                    onPictureError={markPictureFailed}
                    onRemove={handleRemoveRecent}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <h2 className="users-section-title">
              {results.length} result{results.length === 1 ? '' : 's'}
            </h2>
            {error && <div className="users-error"><p>Error: {error}</p></div>}
            {results.length === 0 ? (
              <div className="empty-users">
                <p>No users found matching "{trimmedQuery}".</p>
                {looksLikeUid(trimmedQuery) && (
                  <Link to={`/users/${trimmedQuery}`} className="users-open-uid">
                    Open {trimmedQuery} as a UID
                  </Link>
                )}
              </div>
            ) : (
              <div className="users-list">
                {results.map(user => (
                  <UserCard
                    key={user.uid}
                    user={user}
                    pictureUrl={pictureUrls.get(user.uid)}
                    onPictureError={markPictureFailed}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UsersPage;
